import React, { useEffect, useRef, useState } from "react";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";

interface UseStaticMapResult {
  containerRef: React.RefObject<HTMLDivElement>;
  detailsSpacing: { w: number; h: number } | null;
  mapLoading: boolean;
}

const useStaticMapHook = (opts: {
  aspectRatio?: number;
  minHeight?: number;
  loading: boolean;
}): UseStaticMapResult => {
  const currentBreakpoint = useCurrentBreakpoint();
  const aspectRatio = opts?.aspectRatio ?? 0.75;
  const minHeight = opts?.minHeight ?? 300;
  const [detailsSpacing, setDetailsSpacing] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const lastWidthRef = useRef<number | null>(null);
  const [mapLoading, setMapLoading] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const computeSize = () => {
      const w = el.clientWidth || el.offsetWidth;
      if (!w || w <= 0) return;
      const widthChanged =
        lastWidthRef.current === null ||
        Math.abs((lastWidthRef.current ?? 0) - w) >= 2;
      if (!widthChanged) return;

      if (!debounceTimerRef.current) {
        setMapLoading(true);
      } else {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = window.setTimeout(() => {
        const currentW = el.clientWidth || el.offsetWidth;
        if (!currentW || currentW <= 0) {
          debounceTimerRef.current = null;
          return;
        }
        const currentH = Math.max(
          minHeight,
          Math.round(currentW * aspectRatio),
        );
        setDetailsSpacing({ w: currentW, h: currentH });
        lastWidthRef.current = currentW;
        setMapLoading(false);
        debounceTimerRef.current = null;
      }, 500);
    };

    computeSize();

    const ro = new ResizeObserver(() => {
      computeSize();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [currentBreakpoint, aspectRatio, minHeight, opts.loading]);

  return { containerRef, detailsSpacing, mapLoading };
};

export { useStaticMapHook };
